import * as loadjs from 'loadjs'
import { AuctionHouse } from '../auctionHouse/AuctionHouse'
import { DFP_ID, MAX_TARGETING_LENGTH } from '../constants'
import { Bidder } from '../enums'
import { LoadJsBundle } from '../enums/LoadJsBundle'
import EventManager from '../eventManager/EventManager'
import { Device } from '../helpers'
import { getQueryParam, queryParamHas } from '../helpers/getQueryParam'
import { formatFloor } from '../helpers/numbers'
import UTM from '../helpers/UTM'
import WebModel from '../models/WebModel'
import AmazonAdapter from '../prebidFacade/amazonAdapter/AmazonAdapter'
import { AuctionResults } from '../prebidFacade/AuctionResults'
import { RecipeCardTracker } from '../recipeCardTracker/RecipeCardTracker'
import Session from '../session/Session'
import { Slot } from '../slots/Slot'
import SlotRepository from '../slots/SlotRepository'
import SizeLiftTest from '../sizeLiftTest/SizeLiftTest'
import Keywords from '../keywords/Keywords'
import { AdXAdType } from '../enums/AdType'
import PageIdLookup from '../pageIdLookup/PageIdLookup'
import { USP } from '../cmp'
import { MediaType } from '../enums/MediaType'
import {
  calculateUrTargeting,
  calculateUrpTargeting
} from './helpers/targetingCalculators'
import { meetsOutstreamCriteria } from '../outstream/hasOutstream'
import { LiftTest } from '../liftTest/LiftTest'
import GrowFaves from '../growFaves/GrowFaves'
import { Flooring } from '../flooring/Flooring'

export const GOOGLETAG_URL = '//securepubads.g.doubleclick.net/tag/js/gpt.js'
export const GOOGLETAG_BUNDLE = LoadJsBundle.googletag
export const BANNER_LINE_ITEM_VERSION = '4'
export const OUTSTREAM_LINE_ITEM_VERSION = '1_out'

export default class GoogletagFacade {
  private _pubads: googletag.PubAdsService

  constructor(
    private model: WebModel,
    private auctionHouse: AuctionHouse,
    private keywordService: Keywords,
    private _window: Window = window
  ) {
    // Prime namespaces in case not loaded
    window.googletag = window.googletag || {}
    window.googletag.cmd = window.googletag.cmd || []

    if (isGoogletagLoaded()) {
      window.googletag.cmd.push(() => {
        loadjs.done(GOOGLETAG_BUNDLE)
      })
    } else {
      loadjs(GOOGLETAG_URL, GOOGLETAG_BUNDLE)
    }

    EventManager.on(EventManager.events.bidReady, this.displayAds.bind(this))

    GoogletagFacade.onReady<void>(() => {
      this.addEventListeners(auctionHouse)
      this.addPageLevelTargeting(model)

      if (Device.isMobileOrTablet) {
        this.pubads.setSafeFrameConfig({
          allowOverlayExpansion: false,
          allowPushExpansion: false,
          sandbox: true,
          // @ts-ignore
          useUniqueDomain: true
        })
      } else {
        this.pubads.setSafeFrameConfig({
          allowOverlayExpansion: true,
          allowPushExpansion: true,
          sandbox: true,
          // @ts-ignore
          useUniqueDomain: true
        })
      }

      this.pubads.collapseEmptyDivs()
      this.pubads.enableSingleRequest()
      // Disable initial load since we use refresh to display new ads
      this.pubads.disableInitialLoad()
      this.googletag.enableServices()
    })
  }

  static onReady<T>(cb: Function): Promise<T> {
    if (window.googletag && window.googletag.pubadsReady) {
      return Promise.resolve(cb())
    }

    // Anything returned by these cb's will be resolved after GPT is ready
    return new Promise((resolve, reject) => {
      loadjs.ready(GOOGLETAG_BUNDLE, {
        success: () => {
          window.googletag.cmd.push(() => resolve(cb()))
        },
        error: (deps) => reject(`Error while loading ${deps}`)
      })
    }).then((result) => result as T)
  }

  get googletag(): googletag.Googletag {
    return this._window.googletag
  }

  get pubads(): googletag.PubAdsService {
    this._pubads = this._pubads || this._window.googletag.pubads()

    return this._pubads
  }

  private async setSlotTargeting(
    auctionResults: AuctionResults
  ): Promise<AuctionResults> {
    const { slot } = auctionResults
    const amazonTargeting = AmazonAdapter.amazonTargeting[slot.id] || {}

    const {
      hbCount,
      highestBid,
      currentBidWon
    } = this.auctionHouse.getHighestBidData(slot, auctionResults)
    const targeting = (highestBid && highestBid.adserverTargeting) || undefined
    const targetingBid = targeting && targeting.hb_pb ? targeting.hb_pb : 0
    // cache for re-use if not the winner
    slot.highestPrebid = highestBid

    const playerGroup =
      window.MediavineVideo && window.MediavineVideo.playerGroup
    const pageId = await PageIdLookup.getPageId()
    const partnerLift = LiftTest.new(this.model).getPartnerLiftValue()
    const displayFloor = Flooring.getDisplayFloor(this.model, slot.adUnitId)
    const outstreamFloor = Flooring.getOutstreamFloor(this.model)
    slot
      .setTargeting({
        ...targeting,
        ...auctionResults.getPrebidBidderTargeting(),
        ...this.keywordService.getResults(),
        partnerLift,
        hb_bid: (targeting && targeting.hb_bid) || 'no_bid',
        slot_id: slot.id,
        hb_bidder:
          targeting && targeting.hb_bidder
            ? targeting.hb_bidder
            : `no_bidder_${currentBidWon ? '1' : '0'}`,
        hb_count: String(hbCount),
        hb_pool: currentBidWon ? '1' : '0',
        hb_pmp: highestBid && highestBid.dealId ? '1' : '0',
        UR: calculateUrTargeting(targetingBid || displayFloor),
        URP: calculateUrpTargeting(targetingBid, outstreamFloor),
        OE: meetsOutstreamCriteria(slot) ? '1' : '0',
        slot_number: slot.number.toString(),
        slot: slot.adUnitId,
        refresh: slot.isRefreshing ? '1' : '0',
        refresh_count: slot.refreshes > 0 ? slot.refreshes : undefined,
        amznbid: amazonTargeting.amznbid,
        amzniid: amazonTargeting.amzniid,
        amznp: amazonTargeting.amznp,
        amznsize: amazonTargeting.amznsize,
        bidFloor: formatFloor(displayFloor),
        player_group: playerGroup,
        ccpa: USP.uspString,
        pid: pageId.toString(),
        psa: this.model.psas ? this.model.psas.map((psa) => psa.gam_key) : [],
        timeout: slot.timeout.targetingCode,
        xid: window.$mediavine.xid || '0',
        gid: GrowFaves.hasLoggedInUser ? '1' : '0'
      })
      .applyTargeting()

    return auctionResults
  }

  private displayAds(auctionResultSet: Array<AuctionResults>): void {
    GoogletagFacade.onReady(async () => {
      const promises = auctionResultSet.map((bid) => this.setSlotTargeting(bid))
      await Promise.all(promises)
      const gSlots = auctionResultSet.map((bid) => bid.slot.gSlot)
      this.pubads.refresh(gSlots)
    })
  }

  static registerSlot(slot: Slot): googletag.Slot {
    const sizeLiftTest = new SizeLiftTest(slot)

    slot.sizes = sizeLiftTest.slotSizes

    const slotName = `/${DFP_ID}/${slot.model.adunit}/${slot.slotName ||
      'other'}`

    let gSlot: googletag.Slot
    if (slot.outOfPage) {
      gSlot = window.googletag.defineOutOfPageSlot(
        slotName,
        (googletag.enums.OutOfPageFormat.INTERSTITIAL as unknown) as string
      )
    } else {
      gSlot = window.googletag.defineSlot(slotName, slot.sizes, slot.id)
    }

    if (gSlot) {
      gSlot.addService(window.googletag.pubads())
      window.googletag.display(gSlot)
    }

    return gSlot
  }

  /**
   * Registers event listeners with GPT that will publish
   * through our own event manager.
   */
  private addEventListeners(auctionHouse: AuctionHouse): boolean {
    this.pubads.addEventListener(
      EventManager.events.slotRenderEnded,
      (e: googletag.events.SlotRenderEndedEvent) => {
        const slot = SlotRepository.getSlotByGSlot(e.slot)
        if (slot) {
          EventManager.trigger(EventManager.events.slotRenderEnded, e, slot)
        }
      }
    )

    return true
  }

  private addPageLevelTargeting(model: WebModel): boolean {
    const date = new Date()
    const recipeCardTracker = new RecipeCardTracker()

    this.pubads
      .setTargeting('site', model.slug.substr(0, MAX_TARGETING_LENGTH))
      .setTargeting(
        'path',
        window.location.pathname.substr(0, MAX_TARGETING_LENGTH)
      )
      .setTargeting('secure', window.location.protocol === 'https:' ? '1' : '0')
      .setTargeting('sessiondepth', Session.depth.toString())
      .setTargeting(
        'optout',
        model.optouts.map((optout) => optout.slug)
      )
      .setTargeting(
        'categories',
        model.categories.map((c) => c.slug.substr(0, MAX_TARGETING_LENGTH))
      )
      .setTargeting('generator', 'web')
      .setTargeting('bucket', (Math.floor(Math.random() * 100) + 1).toString())
      .setTargeting('referrer', Session.referrer)
      .setTargeting('utm_source', UTM.utmSource || '')
      .setTargeting('utm_campaign', UTM.utmCampaign || '')
      // Day of Week (0-6)
      .setTargeting('dow', date.getUTCDay().toString())
      // Day of Month (1-31)
      .setTargeting('day', date.getUTCDate().toString())
      // Month (1-12)
      .setTargeting('month', String(date.getUTCMonth() + 1))
      // Hour (0-23)
      .setTargeting('hour', date.getUTCHours().toString())
      .setTargeting('wrapper_group', model.versionGroup.name)

    if (model.bidders.find((b) => b.name === Bidder.google)) {
      this.pubads.setTargeting('google', '1').setTargeting('native', '1')
    }

    if (recipeCardTracker.recipeCard) {
      this.pubads.setTargeting('rcard', recipeCardTracker.recipeCard)
    }

    Object.keys(model.customTargeting).forEach((k) =>
      this.pubads.setTargeting(k, model.customTargeting[k])
    )

    let testParams = getQueryParam('test')
    // Convert testParams to an array if it's not already
    testParams = testParams
      ? Array.isArray(testParams)
        ? testParams
        : [testParams]
      : []

    const placeholdersIndex = testParams.indexOf('placeholders')
    // Force all ads to be placeholder ads.
    if (placeholdersIndex !== -1 || model.testSite) {
      this.pubads.setTargeting('google', '0')

      // Convert 'placeholders' to 'houseads' if it exists, else push 'houseads'
      if (placeholdersIndex !== -1) {
        testParams[placeholdersIndex] = 'houseads'
      } else {
        testParams.push('houseads')
      }
    }
    if (testParams.length > 0) {
      this.pubads.setTargeting('test', testParams)
    }

    return true
  }
}

function isGoogletagLoaded(): boolean {
  const hasScript = !!document.querySelector(`script[src$="${GOOGLETAG_URL}"]`)

  // getVersion is one of the first keys added to googletag when gpt.js is loading.
  // Checking for this should tell us if googletag has already been loaded even if the GOOGLETAG_URL
  // wasn't used.
  const seemsLikeItsLoading = window.googletag.getVersion !== undefined

  return hasScript || seemsLikeItsLoading
}

declare global {
  namespace googletag {
    /* eslint-disable-next-line  */
    interface Googletag {
      display(slot?: Element | googletag.Slot): void
      enums: {
        OutOfPageFormat: {
          BOTTOM_ANCHOR: number
          INTERSTITIAL: number
          REWARDED: number
          TOP_ANCHOR: number
        }
      }
    }
    /* eslint-disable-next-line  */
    interface Slot {
      getAdUnitPath(): string
    }
    namespace events {
      interface ImpressionViewableEvent {
        size: Array<number>
      }
      /* eslint-disable-next-line  */
      interface SlotOnloadEvent {
        size: Array<number>
      }
      /* eslint-disable-next-line  */
      interface SlotVisibilityChangedEvent {
        size: Array<number>
      }
    }
  }
}
